import type { GateDecisionRecord } from '@/types'

/**
 * Bug #21 [v2] — backend-derived tuning knobs for the W3.1 / W3.2
 * validators. ``ContentAwareG4Gate`` fetches the live values from
 * ``GET /api/test/g4-settings`` on mount and threads them through
 * here; the fallbacks below match ``settings.rationale_min_chars``
 * (default 20) and ``settings.bulk_accept_threshold`` (default 5)
 * so a 404 (stale frontend hitting an older backend) degrades to
 * the pre-Bug-#21 behaviour instead of crashing the panel.
 */
export interface G4ValidationSettings {
  rationaleMinChars?: number
  bulkAcceptThreshold?: number
}
export const RATIONALE_MIN_CHARS_FALLBACK = 20
export const BULK_ACCEPT_THRESHOLD_FALLBACK = 5
export const MAX_SUBMIT_RETRIES_FALLBACK = 3

// Default rationale string used whenever the client programmatically
// flips a Q9 panel to ``accept_original`` (bulk auto-approve, polish
// auto-fill, post-422 fallback). The backend's W3.1 validator
// (g4_degradation.py — see ``rationale_min_chars`` in
// ai_alm.config) rejects ``accept_original`` choices on critical /
// high severity findings when the rationale is shorter than
// ``settings.rationale_min_chars`` (default 20). Without this default
// the architect-initiated "Auto Approve" → "Submit & Reevaluate"
// flow 422s for every flagged row with the
// "accept_original on critical severity requires rationale of >= 20
// chars (W3.1)" diagnostic. The text below comfortably exceeds the
// floor and clearly identifies bulk acceptance in the
// accepted-violations.json audit trail.
export const BULK_ACCEPT_RATIONALE =
  'Bulk auto-approved by architect during G4 ratification; original requirement retained.'

// W3.2 (Hardening): when the submission accepts more than
// ``settings.bulk_accept_threshold`` violations (default 5) in one
// shot, the backend additionally requires a non-empty
// ``batch_rationale`` on at least one decision payload. We piggy-back
// on the same wording as ``BULK_ACCEPT_RATIONALE`` so the audit log
// stays consistent.
export const BULK_BATCH_RATIONALE = BULK_ACCEPT_RATIONALE

// --------------------------------------------------------------------------- //
// Q9 four-option G4 panel (plan §9.1) — types + helpers
// --------------------------------------------------------------------------- //
//
// The backend (``ai_alm.services.g4_degradation.G4Panel.to_dict``)
// stamps a structured payload onto every G4 decision when the
// ``g4_graceful_degradation`` flag is on. Each ``F-###`` decision
// carries:
//
//     payload = {
//       finding_id, requirement_id, rule, severity, before_text,
//       options: [
//         { id, label, summary, enabled, preview_text },  // accept_original
//         { id, label, summary, enabled, preview_text },  // accept_rewrite
//         { id, label, summary, enabled, preview_text },  // manual_edit
//         { id, label, summary, enabled, preview_text },  // reprompt
//       ],
//       choice?: { option_id, manual_edit_text?, reprompt_note?, rationale? }
//     }
//
// The backend's ``_apply_q9_g4_submission`` (api/routes/gates.py) reads
// ``payload.choice`` to dispatch.
export interface Q9Option {
  id: string
  label: string
  summary: string
  enabled: boolean
  preview_text: string
}

export interface Q9Payload {
  finding_id: string
  requirement_id: string
  rule: string
  severity: string
  before_text: string
  options: Q9Option[]
  choice?: {
    option_id?: string
    manual_edit_text?: string
    reprompt_note?: string
    rationale?: string
  }
}

/**
 * Returns the Q9 panel payload when the decision was emitted by the
 * Q9 four-option G4 backend, or ``null`` when it's a legacy v0.6
 * REW-### / placeholder row.
 */
export function readQ9Payload(decision: GateDecisionRecord): Q9Payload | null {
  const payload = decision.payload as Record<string, unknown> | null | undefined
  if (!payload || typeof payload !== 'object') return null
  if (!Array.isArray((payload as { options?: unknown }).options)) return null
  const options = (payload as { options: unknown[] }).options
  // We require at least the ``finding_id`` + ``options`` array; everything
  // else has a sensible fallback below.
  if (!decision.id.startsWith('F-')) return null
  return {
    finding_id: String(payload.finding_id ?? decision.id),
    requirement_id: String(payload.requirement_id ?? ''),
    rule: String(payload.rule ?? ''),
    severity: String(payload.severity ?? ''),
    before_text: String(payload.before_text ?? ''),
    options: options.map((o) => {
      const opt = (o ?? {}) as Record<string, unknown>
      return {
        id: String(opt.id ?? ''),
        label: String(opt.label ?? ''),
        summary: String(opt.summary ?? ''),
        enabled: opt.enabled !== false,
        preview_text: String(opt.preview_text ?? ''),
      }
    }),
    choice: (payload.choice as Q9Payload['choice']) ?? undefined,
  }
}

/**
 * Rebuild ``payload.choice`` from a row's persisted ``status`` /
 * ``answer`` when the choice payload is missing. The backend's
 * ``gate_emitter._merge_with_prior_decisions`` preserves the
 * architect's ``status`` + ``answer`` when re-emitting a gate (e.g.
 * after Submit & Reevaluate kicks off a fresh Phase-4 / Critic pass),
 * but it does NOT carry ``payload.choice`` across the re-emit — the
 * new payload comes straight from the fresh Critic run. So
 * previously-approved rows show up with ``status=approved``,
 * ``answer="accept_original"``, and an *empty* ``payload.choice`` —
 * which the Q9 backend resolver then rejects with
 * ``option_id '' not in [...]`` because every panel still needs a
 * choice to validate.
 *
 * Manifested as the "Approve & Continue keeps re-running and the
 * gate never advances to P5" failure mode: ``computeAutoApproved``
 * only patches *pending* rows, the ``submit`` mutation does not use
 * ``submitWithRecovery``, so the 24 stale-choice rows 422 every
 * single time and the architect is stuck.
 *
 * Falls back to ``accept_original`` (the safest terminal pick) when
 * the prior option is no longer enabled on the freshly-emitted panel
 * — typical when the Critic re-runs and ``accept_rewrite`` is now
 * disabled because the Rewriter's best attempt no longer converges.
 */
export function rehydrateChoiceFromAnswer(
  decision: GateDecisionRecord,
  q9: Q9Payload,
): GateDecisionRecord {
  const answer = (decision.answer ?? '').trim()
  let optionId: string
  let manualEditText = ''
  let repromptNote = ''

  if (
    answer === 'accept_original' ||
    answer === 'accept_rewrite' ||
    answer === 'reprompt'
  ) {
    optionId = answer
  } else if (decision.status === 'edited' && answer) {
    optionId = 'manual_edit'
    manualEditText = answer
  } else {
    // Catch-all — non-canonical answer string on a non-pending row.
    // Default to accept_original since the row is already "resolved"
    // from the architect's perspective and accept_original is the
    // safest terminal pick (logged to accepted-violations.json,
    // excluded from the export bundle).
    optionId = 'accept_original'
  }

  // Verify the target option is enabled on the freshly-emitted panel.
  // The Critic may have re-run between the prior submit and this one
  // (e.g. via Submit & Reevaluate), disabling options that previously
  // worked. Fall back to ``accept_original`` (always enabled) when
  // the desired option is no longer available — same safety net the
  // ``submitWithRecovery`` retry loop relies on, but applied up-front
  // so Approve & Continue (which does not have a retry layer) gets
  // through on the first try.
  const targetOption = q9.options.find((o) => o.id === optionId)
  if (!targetOption?.enabled) {
    optionId = 'accept_original'
    manualEditText = ''
    repromptNote = ''
  }

  // accept_original on critical/high severity requires a non-empty
  // rationale of >= settings.rationale_min_chars (W3.1). Seed the
  // bulk default so the very first submit attempt passes validation.
  const rationale =
    optionId === 'accept_original' ? BULK_ACCEPT_RATIONALE : ''

  const nextChoice: Q9Payload['choice'] = {
    option_id: optionId,
    manual_edit_text: manualEditText,
    reprompt_note: repromptNote,
    rationale,
  }
  return {
    ...decision,
    payload: {
      ...((decision.payload as Record<string, unknown>) ?? {}),
      choice: nextChoice,
    },
  }
}

/**
 * Pre-submit sanitiser shared by ``submit`` (Approve & Continue) and
 * ``submitReevaluate``'s pre-flight pass. Three responsibilities:
 *
 *   * **Rehydration** — for any non-pending Q9 row whose
 *     ``payload.choice`` is missing (typical after the backend
 *     re-emits the gate; see :func:`rehydrateChoiceFromAnswer`),
 *     rebuild the choice payload from ``status`` + ``answer``.
 *     Without this step Approve & Continue 422s with
 *     ``option_id '' not in [...]`` for every previously-approved
 *     row that survived the re-emit.
 *   * W3.1 — seed ``BULK_ACCEPT_RATIONALE`` on every Q9
 *     ``accept_original`` row whose rationale is empty or sub-floor
 *     (< 20 chars). This satisfies
 *     ``g4_degradation.validate_submission``'s critical/high
 *     rationale check on the very first submission attempt.
 *   * W3.2 — when the post-sanitisation set has more than
 *     ``settings.bulk_accept_threshold`` (default 5)
 *     ``accept_original`` rows, attach ``batch_rationale`` to the
 *     first decision payload so the bulk-accept validator passes
 *     without needing a retry.
 *
 * Pure function — the input list is never mutated; rows that don't
 * need patching are returned by reference. Idempotent: re-running on
 * a previously-seeded draft is a no-op (rehydration short-circuits
 * when ``payload.choice.option_id`` is already set; W3.1 short-
 * circuits when the rationale meets the floor; the ``existingBatch``
 * string check covers W3.2).
 */
export function sanitiseQ9Submission(
  decisions: GateDecisionRecord[],
  opts: G4ValidationSettings = {},
): GateDecisionRecord[] {
  const rationaleMinChars =
    opts.rationaleMinChars ?? RATIONALE_MIN_CHARS_FALLBACK
  const bulkAcceptThreshold =
    opts.bulkAcceptThreshold ?? BULK_ACCEPT_THRESHOLD_FALLBACK

  // Pass 1 — rehydrate missing ``payload.choice`` for any non-pending
  // Q9 row. See :func:`rehydrateChoiceFromAnswer` for the full
  // background. Idempotent: rows whose choice is already populated
  // are returned by reference.
  const rehydrated = decisions.map((d) => {
    const q9 = readQ9Payload(d)
    if (!q9) return d
    if (q9.choice?.option_id) return d
    if (d.status === 'pending') return d
    return rehydrateChoiceFromAnswer(d, q9)
  })

  // Pass 2 — W3.1 rationale floor seed.
  const sanitised = rehydrated.map((d) => {
    const q9 = readQ9Payload(d)
    if (!q9) return d
    if (q9.choice?.option_id !== 'accept_original') return d
    const existing = (q9.choice?.rationale ?? '').trim()
    if (existing.length >= rationaleMinChars) return d
    const nextChoice: Q9Payload['choice'] = {
      option_id: 'accept_original',
      manual_edit_text: q9.choice?.manual_edit_text ?? '',
      reprompt_note: q9.choice?.reprompt_note ?? '',
      rationale: BULK_ACCEPT_RATIONALE,
    }
    return {
      ...d,
      payload: {
        ...((d.payload as Record<string, unknown>) ?? {}),
        choice: nextChoice,
      },
    }
  })
  const acceptOriginalCount = sanitised.filter((d) => {
    const q9 = readQ9Payload(d)
    return q9?.choice?.option_id === 'accept_original'
  }).length
  if (!(acceptOriginalCount > bulkAcceptThreshold)) return sanitised
  const firstIdx = sanitised.findIndex(
    (d) => d.payload && typeof d.payload === 'object',
  )
  if (firstIdx < 0) return sanitised
  const existing = (sanitised[firstIdx].payload as Record<string, unknown>) ?? {}
  const existingBatch = (existing as { batch_rationale?: unknown }).batch_rationale
  if (typeof existingBatch === 'string' && existingBatch.trim().length > 0) {
    return sanitised
  }
  return sanitised.map((d, i) =>
    i === firstIdx
      ? {
          ...d,
          payload: {
            ...existing,
            batch_rationale: BULK_BATCH_RATIONALE,
          },
        }
      : d,
  )
}

/**
 * Bug #22 [v2] — in-memory sanitisation pass for rehydrated gate
 * drafts.
 *
 * Wraps ``sanitiseQ9Submission`` with a row-level reference-equality
 * diff so callers (specifically the ``useEffect`` in
 * ``ContentAwareG4Gate``) can decide whether to write the sanitised
 * array back to the zustand store. The sanitiser already preserves
 * row references for any row that doesn't need patching (W3.1
 * short-circuit + W3.2 single-write), so ``before[i] === after[i]``
 * is a reliable cheap "did this row change?" check — no
 * ``fast-deep-equal`` import needed.
 *
 * Idempotency: re-running on an already-sanitised draft returns
 * ``changed: false`` and a sanitised array whose row references all
 * match the input. That makes the gate-component's useEffect safe to
 * depend on ``decisions`` without an infinite render loop (a no-op
 * sanitisation does not call ``setDecisions`` ⇒ no re-render ⇒ no
 * second effect firing).
 */
export function sanitiseInMemoryDraft(
  decisions: GateDecisionRecord[],
  opts: G4ValidationSettings = {},
): { sanitised: GateDecisionRecord[]; changed: boolean } {
  const sanitised = sanitiseQ9Submission(decisions, opts)
  const changed = sanitised.some((row, i) => row !== decisions[i])
  return { sanitised, changed }
}

/**
 * Identify the decisions where the 422 auto-recovery loop wiped the
 * architect's typed ``manual_edit_text`` / ``reprompt_note``. Used by
 * the Submit & Reevaluate path to surface a "lost typed text" warning
 * toast after ``submitWithRecovery`` returns (Bug #19).
 *
 * ``before`` and ``after`` MUST be index-aligned (both are
 * ``.map``-derived from the same source array so the alignment
 * holds in practice). Returns the finding ids whose
 * ``manual_edit_text`` or ``reprompt_note`` was non-empty in
 * ``before`` and empty (``''``) in ``after``.
 *
 * The predicate intentionally does NOT gate on ``option_id``
 * transitions because ``coerceToAcceptOriginal`` clears both fields
 * unconditionally — even when the row was already on
 * ``accept_original`` pre-retry. ``sanitiseQ9Submission`` only
 * mutates ``rationale``, so it can't introduce false positives here.
 *
 * Pure, no side effects.
 */
export function findRecoveredWithDraftLoss(
  before: GateDecisionRecord[],
  after: GateDecisionRecord[],
): string[] {
  const lost: string[] = []
  const length = Math.min(before.length, after.length)
  for (let i = 0; i < length; i += 1) {
    const beforeChoice = readQ9Payload(before[i])?.choice ?? {}
    const afterChoice = readQ9Payload(after[i])?.choice ?? {}
    const lostManualEdit =
      (beforeChoice.manual_edit_text ?? '').trim().length > 0 &&
      (afterChoice.manual_edit_text ?? '') === ''
    const lostReprompt =
      (beforeChoice.reprompt_note ?? '').trim().length > 0 &&
      (afterChoice.reprompt_note ?? '') === ''
    if (lostManualEdit || lostReprompt) {
      lost.push(before[i].id)
    }
  }
  return lost
}
